import { Avatar, Button, Flex, Group, Input, Text, Tooltip } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { IconEdit, IconInfoSquare, IconPhoto, IconSearch } from "@tabler/icons-react";
import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { PartnerApi } from "../../apis";
import { IGeneral } from "../../interfaces/IGeneral";
import { IPartner } from "../../interfaces/IPartner";
import { IReference } from "../../models/General";
import { Dialog } from "../../utils/confirm-modal";
import { dateSecFormat } from "../../utils/date";
import HttpHandler from "../../utils/http-handler";
import { Message } from "../../utils/message";
import { renderName } from "../../utils/render-name";
import { PageFilter } from "../layout";
import { ColumnType, ITableRef, Table } from "../table";
import { Tag } from "../tags";
import RenderText from "../ui/render-text";

export function PartnerManagementPartnerEntityList() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [queryDebounced] = useDebouncedValue(query, 400);
  const departmentUnits = useSelector((state: { general: IGeneral }) => state.general.departmentUnits);
  const legalEntityTypes = useSelector((state: { general: IGeneral }) => state.general.legalEntityTypes);
  const partnerTypes = useSelector((state: { general: IGeneral }) => state.general.partnerTypes);
  const partnerRegisterStatus = useSelector((state: { general: IGeneral }) => state.general.partnerRegisterStatus);
  const partnerRegistrationType = useSelector((state: { general: IGeneral }) => state.general.partnerRegistrationType);
  const ref = useRef<ITableRef>(null);

  const columns = useHeader({
    legalEntityTypes,
    departmentUnits,
    partnerTypes,
    partnerRegisterStatus,
    partnerRegistrationType,
    onClick: (key, item) => {
      switch (key) {
        case "edit": {
          navigate(`/partner-management/partner/COMPANY/edit/${item.id}/1`);
          break;
        }
        case "detail": {
          navigate(`/partner-management/partner/COMPANY/detail/${item.id}/1`);
          break;
        }
        case "remove": {
          Dialog.confirm("Та үүнийг устгахдаа итгэлтэй байна уу?", async (key) => {
            switch (key) {
              case "confirm": {
                try {
                  await PartnerApi.remove(`${item?.id}`);
                  reload();
                  Message.success("Ажилтан амжилттай устгагдлаа");
                } catch (err) {
                  Message.error((err as HttpHandler)?.message!);
                }
                break;
              }
              default:
            }
          });
          break;
        }
      }
    },
  });

  const reload = async () => {
    ref.current?.reload();
  };

  return (
    <>
      <PageFilter
        left={[
          <Input
            leftSectionWidth={120}
            onChange={(e) => setQuery(e.currentTarget.value)}
            placeholder="хайх"
            rightSection={<IconSearch size={18} style={{ display: "block", opacity: 0.5 }} />}
            leftSection={
              <Group w={"calc(100% - 20px)"} align="center">
                <Text size="sm" fw={450} c="#44566C">
                  {"Хайлт хийх"}
                </Text>
                <Text>:</Text>
              </Group>
            }
            key={1}
          />,
        ]}
      />
      <Table
        ref={ref}
        name="partner-management-partner-entity.list"
        columns={columns}
        filters={{ query: queryDebounced, type: "COMPANY" }}
        loadData={(data) => PartnerApi.list(data!)}
      />
    </>
  );
}

type HeaderProps = {
  onClick: (key: string, record: IPartner) => void;
  departmentUnits: any;
  legalEntityTypes: IReference[];
  partnerTypes: IReference[];
  partnerRegisterStatus: IReference[];
  partnerRegistrationType: IReference[];
};

const useHeader = ({ onClick, legalEntityTypes, partnerTypes, partnerRegisterStatus, partnerRegistrationType }: HeaderProps): ColumnType<IPartner>[] => [
  {
    title: "#",
    width: "30px",
    render: (_r, index) => <RenderText text={index + 1} />,
  },
  {
    title: "Үйлдэл",
    align: "left",
    dataIndex: "sku",
    render: (record) => {
      return (
        <Flex gap="sm">
          <Tooltip label="Засварлах" position="bottom">
            <Button variant="light" radius={100} w={35} h={35} p={0} onClick={() => onClick("edit", record)}>
              <IconEdit />
            </Button>
          </Tooltip>
          <Tooltip label="Дэлгэрэнгүй" position="bottom">
            <Button variant="light" radius={100} w={35} h={35} p={0} onClick={() => onClick("detail", record)}>
              <IconInfoSquare />
            </Button>
          </Tooltip>
        </Flex>
      );
    },
  },
  {
    title: "ТТД",
    sorter: true,
    dataIndex: "regNumber",
    render: (record) => <RenderText text={record?.regNumber || "-"} />,
  },
  {
    title: "Партнер код",
    sorter: true,
    dataIndex: "refCode",
    render: (record) => <RenderText text={record?.refCode || "-"} />,
  },
  {
    title: "Партнер нэр",
    sorter: true,
    dataIndex: "businessName",
    render: (record) => <RenderText text={record?.businessName || "-"} />,
  },
  {
    title: "Төрөл",
    sorter: true,
    dataIndex: "sku",
    render: (record) => (
      <Tag color={record.type === "CITIZEN" ? "green" : "blue"} name={partnerTypes.find((item: IReference) => item.code === record?.type)?.name || "-"} />
    ),
  },
  {
    title: "ААН",
    sorter: true,
    dataIndex: "legalEntityType",
    render: (record) => <RenderText text={legalEntityTypes.find((item: IReference) => item.code === record.legalEntityType)?.name || "-"} />,
  },
  {
    title: "Худалдан авагч эсэх",
    sorter: true,
    dataIndex: "isBuyer",
    render: (record) => <Tag name={record?.isBuyer ? "Тийм" : "Үгүй"} color={record?.isBuyer ? "green.5" : "red.5"} />,
  },
  {
    title: "Нийлүүлэгч эсэх",
    sorter: true,
    dataIndex: "isSupplier",
    render: (record) => <Tag name={record?.isSupplier ? "Тийм" : "Үгүй"} color={record?.isSupplier ? "green.5" : "red.5"} />,
  },
  {
    title: "Бүртгэлийн статус",
    sorter: true,
    dataIndex: "registerStatus",
    render: (record) => {
      return (
        <Tag
          color={partnerRegisterStatus.find((item: IReference) => item.code === record.registerStatus)?.color || "gray"}
          name={partnerRegisterStatus.find((item: IReference) => item.code === record.registerStatus)?.name || "-"}
        />
      );
    },
  },
  {
    title: "Бүртгэсэн",
    sorter: true,
    dataIndex: "isActive",
    render: (record) => {
      return (
        <Flex align="center" gap="sm">
          <Avatar src={record?.regUser?.avatar}>
            <IconPhoto />
          </Avatar>
          <RenderText text={renderName(record?.regUser)} />
        </Flex>
      );
    },
  },
  {
    title: "Бүртгэсэн огноо",
    sorter: true,
    dataIndex: "isActive",
    render: (record) => <RenderText text={dateSecFormat(record?.createdAt)} />,
  },
  {
    title: "Бүртгэл төрөл",
    sorter: true,
    dataIndex: "registerType",
    render: (record) => (
      <Tag
        name={partnerRegistrationType?.find((item: IReference) => item?.code === record?.registrationType)?.name || "-"}
        color={partnerRegistrationType?.find((item: IReference) => item?.code === record?.registrationType)?.color || "blue.5"}
      />
    ),
  },
];
